import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, LOGO_URL_DARK, LOGO_URL_LIGHT } from '../App';
import { useTheme } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Plus, 
  Shield, 
  Globe, 
  Zap, 
  MapPin, 
  MessageCircle,
  CheckCircle,
  Sparkles,
  ArrowRight
} from 'lucide-react';

// Translations
const translations = {
  nl: {
    tagline: 'Jouw Marktplaats, Jouw Eiland.',
    subtitle: 'Your Buy Site is niet zomaar een website; het is de slimste manier om te handelen op de Caribbean. Geen gedoe, geen kosten, alleen resultaat.',
    mission: {
      title: 'Onze Missie',
      text: 'Wij geloven dat handelen op een eiland gebaseerd moet zijn op vertrouwen en gemak. Onze missie is om de barrières van traditioneel verkopen te doorbreken met een modern, veilig en razendsnel platform dat werkt voor iedereen.'
    },
    benefits: {
      title: 'Waarom Your Buy Site?',
      items: [
        { icon: CheckCircle, title: 'Echt 100% Gratis', description: 'Geen commissies, geen "premium" kosten voor basisfuncties. Winst is volledig van jou.' },
        { icon: Shield, title: 'Slimme Verificatie', description: 'Wij checken accounts, zodat jij met een gerust hart kunt afspreken.' },
        { icon: Globe, title: 'Meertalig', description: 'Schakel naadloos tussen Nederlands, Engels en Spaans.' },
        { icon: Zap, title: 'PWA Technologie', description: 'Geen trage website, maar een app-ervaring direct in je browser.' },
        { icon: MapPin, title: 'Eiland-Focus', description: 'Specifieke filters voor jouw eiland: Curaçao, Aruba of Bonaire.' },
        { icon: MessageCircle, title: 'Direct Contact', description: 'Geïntegreerde chat en WhatsApp die werkt zoals je gewend bent.' }
      ]
    },
    power: {
      quote: 'Wij bouwen niet alleen een platform, wij bouwen de digitale economie van de eilanden.',
      items: [
        { icon: Sparkles, title: 'Grenzeloos', description: 'Kies je eigen eiland, je eigen valuta en je eigen taal.' },
        { icon: Shield, title: 'Veilig', description: 'Meldfuncties en actieve moderatie houden de community schoon.' },
        { icon: Zap, title: 'Razendsnel', description: 'Van foto naar live advertentie in minder dan 60 seconden.' }
      ]
    },
    stats: {
      items: [
        { value: '0,-', label: 'Kosten per advertentie' },
        { value: '3', label: 'Talen ondersteund' },
        { value: '3', label: 'Eilanden' },
        { value: '∞', label: 'Mogelijkheden' }
      ]
    },
    cta: {
      title: 'Klaar om te verkopen?',
      subtitle: 'Sluit je aan bij duizenden eilanders die al slimmer verkopen.',
      button: 'Plaats Gratis Advertentie',
      browseButton: 'Bekijk Marktplaats'
    }
  },
  en: {
    tagline: 'Your Marketplace, Your Island.',
    subtitle: 'Your Buy Site is not just a website; it\'s the smartest way to trade in the Caribbean. No hassle, no costs, just results.',
    mission: {
      title: 'Our Mission',
      text: 'We believe that trading on an island should be based on trust and convenience. Our mission is to break down the barriers of traditional selling with a modern, safe, and lightning-fast platform that works for everyone.'
    },
    benefits: {
      title: 'Why Your Buy Site?',
      items: [
        { icon: CheckCircle, title: '100% Free', description: 'No commissions, no "premium" costs for basic features. The profit is entirely yours.' },
        { icon: Shield, title: 'Smart Verification', description: 'We verify accounts so you can meet up with peace of mind.' },
        { icon: Globe, title: 'Multilingual', description: 'Switch seamlessly between Dutch, English, and Spanish.' },
        { icon: Zap, title: 'PWA Technology', description: 'No slow website, but an app experience right in your browser.' },
        { icon: MapPin, title: 'Island Focus', description: 'Specific filters for your island: Curaçao, Aruba, or Bonaire.' },
        { icon: MessageCircle, title: 'Direct Contact', description: 'Integrated chat and WhatsApp that works just like you\'re used to.' }
      ]
    },
    power: {
      quote: 'We\'re not just building a platform, we\'re building the digital economy of the islands.',
      items: [
        { icon: Sparkles, title: 'Boundless', description: 'Choose your own island, your own currency, and your own language.' },
        { icon: Shield, title: 'Safe', description: 'Report functions and active moderation keep the community clean.' },
        { icon: Zap, title: 'Lightning Fast', description: 'From photo to live listing in less than 60 seconds.' }
      ]
    },
    stats: {
      items: [
        { value: '$0', label: 'Cost per listing' },
        { value: '3', label: 'Languages supported' },
        { value: '3', label: 'Islands' },
        { value: '∞', label: 'Possibilities' }
      ]
    },
    cta: {
      title: 'Ready to sell?',
      subtitle: 'Join thousands of islanders who are already selling smarter.',
      button: 'Place Free Listing',
      browseButton: 'Browse Marketplace'
    }
  },
  es: {
    tagline: 'Tu Mercado, Tu Isla.',
    subtitle: 'Your Buy Site no es solo un sitio web; es la forma más inteligente de comerciar en el Caribe. Sin complicaciones, sin costos, solo resultados.',
    mission: {
      title: 'Nuestra Misión',
      text: 'Creemos que el comercio en una isla debe basarse en la confianza y la comodidad. Nuestra misión es romper las barreras de la venta tradicional con una plataforma moderna, segura y ultrarrápida que funciona para todos.'
    },
    benefits: {
      title: '¿Por qué Your Buy Site?',
      items: [
        { icon: CheckCircle, title: '100% Gratis', description: 'Sin comisiones, sin costos "premium" por funciones básicas. Las ganancias son completamente tuyas.' },
        { icon: Shield, title: 'Verificación Inteligente', description: 'Verificamos las cuentas para que puedas reunirte con tranquilidad.' },
        { icon: Globe, title: 'Multilingüe', description: 'Cambia sin problemas entre holandés, inglés y español.' },
        { icon: Zap, title: 'Tecnología PWA', description: 'No es un sitio web lento, sino una experiencia de app en tu navegador.' },
        { icon: MapPin, title: 'Enfoque en las Islas', description: 'Filtros específicos para tu isla: Curazao, Aruba o Bonaire.' },
        { icon: MessageCircle, title: 'Contacto Directo', description: 'Chat integrado y WhatsApp que funciona como estás acostumbrado.' }
      ]
    },
    power: {
      quote: 'No solo construimos una plataforma, construimos la economía digital de las islas.',
      items: [
        { icon: Sparkles, title: 'Sin Límites', description: 'Elige tu propia isla, tu propia moneda y tu propio idioma.' },
        { icon: Shield, title: 'Seguro', description: 'Las funciones de reporte y la moderación activa mantienen la comunidad limpia.' },
        { icon: Zap, title: 'Ultrarrápido', description: 'De foto a anuncio en vivo en menos de 60 segundos.' }
      ]
    },
    stats: {
      items: [
        { value: '$0', label: 'Costo por anuncio' },
        { value: '3', label: 'Idiomas soportados' },
        { value: '3', label: 'Islas' },
        { value: '∞', label: 'Posibilidades' }
      ]
    },
    cta: {
      title: '¿Listo para vender?',
      subtitle: 'Únete a miles de isleños que ya venden de forma más inteligente.',
      button: 'Publicar Anuncio Gratis',
      browseButton: 'Ver Mercado'
    }
  }
};

const AboutPage = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const t = translations[language] || translations.en;
  const logoUrl = theme === 'dark' ? LOGO_URL_DARK : LOGO_URL_LIGHT;

  const benefitColors = [
    'from-emerald-500 to-green-600',
    'from-blue-500 to-cyan-600',
    'from-violet-500 to-purple-600',
    'from-amber-500 to-orange-500',
    'from-rose-500 to-pink-600',
    'from-teal-500 to-cyan-500'
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      {/* Standard Navbar */}
      <Navbar />
      
      {/* Main Content */}
      <main className="pt-4 pb-24 md:pb-8">
        <div className="max-w-6xl mx-auto px-4">
          
          {/* Logo + Mission Section */}
          <section className="py-8 md:py-12">
            {/* Large Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src={logoUrl}
                alt="Your Buy Site" 
                className="h-20 md:h-28 w-auto"
              />
            </div>
            
            {/* Tagline */}
            <div className="text-center mb-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
                {t.tagline}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {t.subtitle}
              </p>
            </div>

            {/* Mission Card */}
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-3xl border border-primary/20 p-6 md:p-10">
              <div className="flex items-start gap-4 mb-4">
                <img src="/logo.png" alt="Your Buy Site" className="w-16 h-16 md:w-20 md:h-20 object-contain flex-shrink-0" />
                <h2 className="text-2xl md:text-3xl font-bold text-foreground pt-3 md:pt-4">{t.mission.title}</h2>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed md:ml-24">
                {t.mission.text}
              </p>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-8 border-y border-border bg-card/30 -mx-4 px-4 md:mx-0 md:px-0 md:rounded-2xl md:border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {t.stats.items.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{stat.value}</div>
                  <div className="text-sm md:text-base text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Benefits Grid */}
          <section className="py-10 md:py-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
              {t.benefits.title}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {t.benefits.items.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index}
                    className="bg-card rounded-2xl border border-border p-5 md:p-6 hover:border-primary/50 hover:shadow-lg transition-all group"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefitColors[index]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-foreground text-lg mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm md:text-base leading-relaxed">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Quote + Power Section */}
          <section className="py-10 md:py-14 bg-gradient-to-br from-primary/5 via-background to-blue-500/5 -mx-4 px-4 md:mx-0 md:px-8 md:rounded-3xl border-y md:border border-border">
            {/* Quote */}
            <blockquote className="text-center mb-10">
              <p className="text-xl md:text-2xl text-foreground italic font-medium leading-relaxed max-w-3xl mx-auto">
                "{t.power.quote}"
              </p>
            </blockquote>
            
            {/* Power Items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {t.power.items.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-card/80 backdrop-blur-sm rounded-2xl border border-border p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-bold text-foreground text-lg mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-10 md:py-14">
            <div className="bg-gradient-to-r from-primary to-blue-600 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative">
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
                  {t.cta.title} 💰
                </h2>
                <p className="text-white/90 text-lg md:text-xl mb-8 max-w-xl mx-auto">
                  {t.cta.subtitle}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/create-listing"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-colors shadow-lg text-lg"
                    data-testid="cta-create-listing"
                  >
                    <Plus className="w-5 h-5" />
                    {t.cta.button}
                  </Link>
                  <Link
                    to="/marketplace"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-colors border border-white/30 text-lg"
                    data-testid="cta-browse"
                  >
                    {t.cta.browseButton}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutPage;
